import type { SessionUser } from './authClient.js';
import { type AccessRule, grantsAccess } from './useAccess.js';

/**
 * Where a signed-in user belongs, and which routes they may reach.
 *
 * THE BUG THIS EXISTS TO FIX: a crew member signed in and landed on a screen they cannot use.
 * `/login` redirected everyone, unconditionally, to `/reports`, which Employees cannot open at all.
 * The old bounce lived in a `useEffect` inside HomeRoute, so it ran AFTER a render and after firing
 * an overview request guaranteed to 403.
 *
 * HOME IS NOT ONE SCREEN ANY MORE. It used to be the KPI dashboard and nothing else, fed by
 * `GET /api/v1/overview` behind `reports.view`, so an Employee was sent away from it. Home now
 * picks a layout from what the viewer can do (`resolveHomeLayout`), and the crew layout needs only
 * `time.view`. So the rule below asks whether Home HAS a layout for this person instead of asking
 * for one specific permission.
 *
 * WHY CAPABILITY AND NOT ROLE. The obvious rule is `role === 'Employee' → /timesheets`, and that is
 * what used to be here. It breaks on the per-key overrides this product already ships: prod carries
 * override rows today, so "Employee" is not a reliable statement about what somebody can do. A
 * Manager stripped back to time-only would land on an empty Home; an Employee granted `reports.view`
 * would be forced away from a dashboard they can now read. Asking what the user can actually DO
 * answers both, and keeps working when someone edits a role in the permissions screen.
 *
 * STILL NOT AUTHORIZATION. Every rule here mirrors one the api worker enforces independently. This
 * decides where to POINT somebody, never what they are allowed to have.
 */

export interface LandingRoute {
  to: string;
  /** Some routes have required search params; the router will not navigate without them. */
  search?: Record<string, unknown>;
}

/** Shown only when a user can reach nothing at all — see resolveLandingRoute. */
export const NO_ACCESS_ROUTE = '/no-access';

export const HOME_ROUTE: LandingRoute = { to: '/' };

/**
 * The fallback ladder, in the order a user would want it, for the rare account Home has no layout
 * for. Mirrors the nav's own order so the answer is never a destination the sidebar does not also
 * offer.
 *
 * ⚠ The `/jobs` and `/dispatch` rungs are SHADOWED and cannot currently be reached: `jobs.view` is
 * what selects the dispatcher Home, so anybody who satisfies them has already been sent to `/`.
 * They are kept rather than deleted because they are the correct answer the moment the layout rules
 * change, and a ladder missing its top rungs is harder to spot than one carrying an unused pair.
 */
const FALLBACK_DESTINATIONS: Array<{ route: LandingRoute; access: AccessRule }> = [
  { route: { to: '/jobs', search: { show_archived: false } }, access: { permission: 'jobs.view' } },
  { route: { to: '/dispatch', search: { week: undefined } }, access: { permission: 'jobs.view' } },
  { route: { to: '/inventory' }, access: { permission: 'inventory.view' } },
  { route: { to: '/fleet' }, access: { permission: 'fleet.view' } },
  {
    route: { to: '/invoices', search: { show_archived: false } },
    access: { permission: 'invoices.view' },
  },
  { route: { to: '/activity', search: {} }, access: { permission: 'activity.view' } },
];

/**
 * Which Home layout a user gets, or `null` when Home has nothing to show them.
 *
 * DECIDED BY PERMISSION, NOT BY ROLE (DJ, 2026-09-01). A tenant may rewrite the Manager and
 * Employee presets, and production carries per-user override rows, so "Manager" is not a statement
 * about what somebody can do. Asking what they can READ keeps the answer right after somebody edits
 * a role in the permissions screen. The cost is real and accepted: a Manager stripped of
 * `reports.view` gets the crew layout, with no money on it.
 *
 * The api worker guards the same data independently. This decides what to RENDER, never what
 * anybody is allowed to have.
 */
export type HomeLayout = 'owner' | 'dispatcher' | 'crew';

export function resolveHomeLayout(user: SessionUser | null | undefined): HomeLayout | null {
  if (!user) return null;
  // `reports.view` guards `GET /api/v1/overview`, which is the only source the owner layout has.
  if (grantsAccess(user, { permission: 'reports.view' })) return 'owner';
  // `jobs.view` guards `GET /api/v1/dispatch`. Somebody who can read the board but not the money
  // is running the day, so they get the board rather than a week of their own hours. This rule
  // sits ABOVE `time.view` deliberately: a Manager holds both keys, and the board is the more
  // useful of the two answers for them.
  if (grantsAccess(user, { permission: 'jobs.view' })) return 'dispatcher';
  // `time.view` guards `GET /api/v1/timesheets/week-grid`, which the crew layout reads. The worker
  // scopes that response to the caller's own rows for the Employee role, so the crew layout shows
  // one person their own week without a second endpoint.
  if (grantsAccess(user, { permission: 'time.view' })) return 'crew';
  return null;
}

/**
 * The landing route for a user, decided by what they can do.
 *
 *   1. Home has a layout for them (`resolveHomeLayout`)  → Home;
 *   2. otherwise the first nav destination they can open;
 *   3. otherwise the no-access page, because a resolver that cannot resolve still has to land
 *      somewhere and a redirect loop is the worst possible answer.
 *
 * RULE 1 REPLACED TWO RULES. It used to be "`reports.view` → Home, else `time.clock` → Timesheets",
 * which sent every crew member past Home to the timeclock. Home now carries the clock itself, so
 * the detour is gone.
 *
 * The old `time.clock` → `/timesheets` rule was also a latent redirect loop: `/timesheets` is
 * guarded by `time.view` (see ROUTE_ACCESS_ENTRIES), so a user holding `time.clock` WITHOUT
 * `time.view` was sent to a route the root guard then bounced, straight back to here. Every
 * destination named below is one the sender has already been shown to hold.
 */
export function resolveLandingRoute(user: SessionUser | null | undefined): LandingRoute {
  if (!user) return HOME_ROUTE;

  if (resolveHomeLayout(user)) return HOME_ROUTE;

  for (const candidate of FALLBACK_DESTINATIONS) {
    if (grantsAccess(user, candidate.access)) return candidate.route;
  }

  return { to: NO_ACCESS_ROUTE };
}

/**
 * Access rules per route, lifted from the sidebar so the two cannot disagree.
 *
 * MATCHED LONGEST-PREFIX-FIRST, so a detail route inherits its section's rule without being listed
 * — `/jobs/$id` is reachable by exactly the people who can reach `/jobs`. Two exceptions are listed
 * explicitly because they are STRICTER than their section: `/users/permissions` and the payroll
 * sub-routes.
 *
 * Anything absent is `'always'`. That is deliberate for `/`, `/account`, `/support` and the
 * no-access page itself: gating the places a user is sent would create the loop this is meant to
 * avoid. `/` stays ungated because Home now answers every signed-in viewer with a layout they can
 * read; the index route redirects the rare account Home has nothing for (see router.tsx).
 */
const ROUTE_ACCESS_ENTRIES: Array<{ prefix: string; access: AccessRule }> = [
  // Stricter-than-section entries first; the longest-prefix sort below keeps them winning anyway,
  // but listing them together is how somebody notices they exist.
  { prefix: '/users/permissions', access: { permission: 'users.view' } },
  { prefix: '/payroll', access: { minRole: 'Admin' } },
  // The employee forms answer to the keys their own endpoints apply, not the section's view key.
  { prefix: '/employees/new', access: { permission: 'employees.create' } },

  { prefix: '/jobs', access: { permission: 'jobs.view' } },
  { prefix: '/dispatch', access: { permission: 'jobs.view' } },
  { prefix: '/inventory', access: { permission: 'inventory.view' } },
  { prefix: '/fleet', access: { permission: 'fleet.view' } },
  { prefix: '/estimates', access: { minRole: 'Manager' } },
  // Mirrors the estimates rule: the Clients room is the same audience, and the
  // access rule here and the nav item's rule must not disagree.
  { prefix: '/clients', access: { minRole: 'Manager' } },
  { prefix: '/invoices', access: { permission: 'invoices.view' } },
  { prefix: '/service-agreements', access: { permission: 'invoices.view' } },
  { prefix: '/monthly-bills', access: { permission: 'financials.view' } },
  { prefix: '/reports', access: { permission: 'reports.view' } },
  { prefix: '/employees', access: { permission: 'employees.view' } },
  { prefix: '/timesheets', access: { permission: 'time.view' } },
  { prefix: '/qc', access: { permission: 'qc.view' } },
  { prefix: '/users', access: { permission: 'users.view' } },
  { prefix: '/settings', access: { permission: 'settings.view' } },
  // Looser than its section: a person's own notification choices, open to every signed-in user.
  { prefix: '/settings/notifications', access: 'always' },
  { prefix: '/billing', access: { permission: 'billing.view' } },
  { prefix: '/activity', access: { permission: 'activity.view' } },
];

/** Longest prefix first, so `/users/permissions` is matched before `/users`. */
const ROUTE_ACCESS = [...ROUTE_ACCESS_ENTRIES].sort((a, b) => b.prefix.length - a.prefix.length);

/**
 * Routes a prefix cannot express because the stricter segment follows an id. Checked before the
 * prefixes. `/employees/$id/edit` submits to PATCH /employees/:id, which wants employees.edit.
 */
const PATTERN_ACCESS: Array<{ pattern: RegExp; access: AccessRule }> = [
  { pattern: /^\/employees\/[^/]+\/edit\/?$/, access: { permission: 'employees.edit' } },
];

/** The rule guarding a pathname, or `'always'` when nothing guards it. */
export function resolveRouteAccess(pathname: string): AccessRule {
  const pattern = PATTERN_ACCESS.find((entry) => entry.pattern.test(pathname));
  if (pattern) return pattern.access;
  const match = ROUTE_ACCESS.find(
    (entry) => pathname === entry.prefix || pathname.startsWith(`${entry.prefix}/`),
  );
  return match?.access ?? 'always';
}

/** Whether a user may open a pathname at all. */
export function canOpenRoute(user: SessionUser | null | undefined, pathname: string): boolean {
  return grantsAccess(user, resolveRouteAccess(pathname));
}
